import React from "react";
import {
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";

export const ColleagueDialog = ({ isOpen, onClose, colleague }) => {
  if (!isOpen || !colleague) return null;

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleEmail = (email) => {
    window.location.href = `mailto:${email}`;
  };

  // Helper function to get relationship display name
  const getRelationshipDisplay = (relationshipType) => {
    const relationships = {
      "SPOUSE": "Ektefelle",
      "PARTNER": "Partner",
      "PARENT": "Forelder",
      "CHILD": "Barn",
      "SIBLING": "Søsken",
    };
    return relationships[relationshipType] || relationshipType;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Dialog */}
        <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 rounded-full p-3">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {colleague.name}
                </h3>
                <p className="text-sm text-gray-500">Kollega</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Basic Info */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Kontaktinformasjon</h4>

              {/* Business Mobile */}
              {colleague.mobile_work && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Arbeidsmobil
                      </p>
                      <p className="text-sm text-gray-600">
                        {colleague.mobile_work}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-8">
                    <button
                      onClick={() => handleCall(colleague.mobile_work)}
                      className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                    >
                      <PhoneIcon className="h-3 w-3" />
                      <span>Ring</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Private Mobile */}
              {colleague.mobile && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Privat mobil
                      </p>
                      <p className="text-sm text-gray-600">
                        {colleague.mobile}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-8">
                    <button
                      onClick={() => handleCall(colleague.mobile)}
                      className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                    >
                      <PhoneIcon className="h-3 w-3" />
                      <span>Ring</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Email */}
              {colleague.email && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        E-post
                      </p>
                      <p className="text-sm text-gray-600">{colleague.email}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-8">
                    <button
                      onClick={() => handleEmail(colleague.email)}
                      className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors duration-200"
                    >
                      <EnvelopeIcon className="h-3 w-3" />
                      <span>Send e-post</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Department */}
              {colleague.department && (
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Avdeling
                    </p>
                    <p className="text-sm text-gray-600">
                      {colleague.department.displayTitle}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Next of Kin */}
            {colleague.user_next_of_kins &&
              colleague.user_next_of_kins.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900">Neste pårørende</h4>
                  {colleague.user_next_of_kins.map((kin, index) => (
                    <div
                      key={kin.id || index}
                      className="bg-gray-50 rounded-lg p-3"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {kin.name}
                          </p>
                          <span className="text-xs text-gray-500">
                            {getRelationshipDisplay(kin.relationship_type)}
                          </span>
                        </div>

                        {kin.phone_number && (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <PhoneIcon className="h-3 w-3 text-gray-400" />
                              <p className="text-xs text-gray-600">
                                {kin.phone_number}
                              </p>
                            </div>
                            <div className="ml-5">
                              <button
                                onClick={() => handleCall(kin.phone_number)}
                                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors duration-200"
                              >
                                <PhoneIcon className="h-2 w-2" />
                                <span>Ring</span>
                              </button>
                            </div>
                          </div>
                        )}

                        {kin.email && (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <EnvelopeIcon className="h-3 w-3 text-gray-400" />
                              <p className="text-xs text-gray-600">
                                {kin.email}
                              </p>
                            </div>
                            <div className="ml-5">
                              <button
                                onClick={() => handleEmail(kin.email)}
                                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors duration-200"
                              >
                                <EnvelopeIcon className="h-2 w-2" />
                                <span>Send e-post</span>
                              </button>
                            </div>
                          </div>
                        )}

                        {kin.address && (
                          <div className="flex items-center space-x-2">
                            <div className="h-3 w-3 text-gray-400">📍</div>
                            <p className="text-xs text-gray-600">
                              {kin.address}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {/* Additional Info */}
            {colleague.employee_number && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">
                    Ansattnummer
                  </p>
                  <p className="text-sm text-gray-600">
                    {colleague.employee_number}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Lukk
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
